import { useEffect, useState } from "react";
import { HEADER_HEIGHT } from "@/config/data";
import { signOut } from "@/utils/auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setIsPlayMode, setSelectedRetailerId } from "@/store";
import { Avatar, Badge, Button, Indicator, Menu, Select } from "@mantine/core";
import {
  IconLogout,
  IconPlayerStopFilled,
  IconUser,
} from "@tabler/icons-react";
import { auth } from "@/utils/auth";
import { useNeedsReviewCount } from "@/hooks/useNeedsReviewCount";
import { useProfileHome } from "@/hooks/useProfileHome";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  mvpApiListRetailers,
  type RetailerSchema,
} from "@labeling-tools/api/atlas";

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isPlayMode = useAppSelector((state) => state.oos.isPlayMode);
  const selectedRetailerId = useAppSelector(
    (state) => state.oos.selectedRetailerId,
  );

  const [retailers, setRetailers] = useState<RetailerSchema[]>([]);
  const needsReviewCount = useNeedsReviewCount();
  const { profileAsHome, jobsPath } = useProfileHome();

  useEffect(() => {
    mvpApiListRetailers({ query: { page_size: 100 } })
      .then((res) => setRetailers((res.data as any)?.retailers ?? []))
      .catch(() => {});
  }, []);

  let type;
  let color;
  const isJobsTable = location.pathname === jobsPath;
  // The retailer filter shows on the jobs table and on the profile home (when
  // profile-as-home is on), since Profile's "Start Labeling" filters by the
  // selected retailer.
  const isProfileHome = profileAsHome && location.pathname === "/";
  const showRetailerSelect = isJobsTable || isProfileHome;
  const path = location.pathname.split("/").pop();
  if (path?.includes("confirmation-qa")) {
    type = "Confirmation QA";
    color = "blue";
  } else if (path?.includes("confirmation")) {
    type = "Confirmation";
    color = "blue";
  } else if (path?.includes("selection")) {
    type = "Labeling";
    color = "yellow";
  }

  const stopPlayMode = () => {
    dispatch(setIsPlayMode(false));
    navigate(jobsPath);
  };

  const retailerOptions = [
    { value: "", label: "All retailers" },
    ...retailers
      .filter((r) => r.id)
      .map((r) => ({ value: r.id as string, label: r.name })),
  ];

  return (
    <header
      className="bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-10"
      style={{ height: HEADER_HEIGHT }}
    >
      <div className="px-4 h-full flex items-center">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900">
              <Link to="/" onClick={() => dispatch(setIsPlayMode(false))}>
                Out of Stock
              </Link>
            </h1>
            {showRetailerSelect && (
              <Select
                size="xs"
                w={160}
                placeholder="All retailers"
                clearable
                data={retailerOptions}
                value={selectedRetailerId ?? ""}
                onChange={(val) => dispatch(setSelectedRetailerId(val || null))}
              />
            )}
            {type && (
              <Badge color={color} variant="light" size="lg">
                {type}
              </Badge>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {isPlayMode && (
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<IconPlayerStopFilled size={16} />}
                onClick={stopPlayMode}
              >
                Play Mode
              </Button>
            )}
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <Indicator
                  label={needsReviewCount}
                  size={18}
                  color="red"
                  offset={4}
                  disabled={needsReviewCount === 0}
                >
                  <Avatar
                    color="initials"
                    radius="xl"
                    size="sm"
                    name={auth.currentUser?.email ?? undefined}
                    style={{ cursor: "pointer" }}
                  />
                </Indicator>
              </Menu.Target>
              <Menu.Dropdown>
                {auth.currentUser?.email && (
                  <>
                    <Menu.Label>{auth.currentUser.email}</Menu.Label>
                    <Menu.Divider />
                  </>
                )}
                {profileAsHome && (
                  <Menu.Item
                    leftSection={<IconUser size={16} />}
                    onClick={() => navigate("/")}
                  >
                    Profile
                  </Menu.Item>
                )}
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={16} />}
                  onClick={() => {
                    dispatch(setIsPlayMode(false));
                    signOut();
                  }}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
};
